import React from "react";
import { Link } from "react-router-dom";
import Card from "../../../../components/card";
import { EmployeeHours, TeamWeekLoad } from "../../../../interfaces/Dashboard";
import DeltaChip from "./DeltaChip";
import { CAP_TICK, SeriesKind, barTone, loadBar } from "./teamLoad";

/**
 * Carga do time — as mesmas duas séries, ordenadas pela lente ativa.
 *
 * Planejado e apontado são medidas independentes sobre a mesma capacidade, não parcelas de um
 * total: apontar mais do que o planejado é informação, não erro de soma, e é justamente a
 * diferença entre as duas que revela trabalho que o cronograma não capturou.
 *
 * O que muda entre as lentes é qual delas responde pela pergunta — e essa vai por cima, junto do
 * percentual em destaque:
 *   Semana → planejado no topo (o que vai acontecer, ainda dá para redistribuir).
 *   Mês    → apontado no topo (o que já aconteceu), com o Δ contra o mesmo período do anterior.
 * A cor é fixa por série (ver `barTone`), então trocar a ordem não troca o significado.
 *
 * O tique claro marca 100% da capacidade individual: é o que separa "carregado" de "estourado" sem
 * depender de o leitor comparar dois números de cabeça.
 */

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";

const hrs = (n: number) => n.toLocaleString("pt-BR", { maximumFractionDigits: 1 });

interface Row {
  id: number;
  name: string;
  role?: string | null;
  capacity: number;
  /** Série em destaque: a que responde à pergunta da lente e leva o percentual. */
  value: number;
  pct: number;
  primaryKind: SeriesKind;
  /** A outra série, desenhada abaixo como referência. */
  secondary?: number;
  delta?: number | null;
  flag?: string;
}

const SERIES_LABEL: Record<SeriesKind, string> = {
  planned: "planejadas",
  worked: "apontadas",
};

const other = (k: SeriesKind): SeriesKind => (k === "planned" ? "worked" : "planned");

/** Uma trilha de 100% da capacidade, com o preenchimento de uma série. */
const Track: React.FC<{ width: number; className: string; thin?: boolean }> = ({
  width,
  className,
  thin,
}) => (
  <span className={`relative block rounded-md bg-gray-100 dark:bg-white/5 ${thin ? "h-[7px]" : "h-3.5"}`}>
    <span className={`absolute inset-y-0 left-0 rounded ${className}`} style={{ width: `${width}%` }} />
  </span>
);

const Bar: React.FC<{ r: Row }> = ({ r }) => {
  const primary = loadBar(r.value, r.capacity, r.pct);
  const secondary = r.secondary !== undefined ? loadBar(r.secondary, r.capacity) : null;
  const secondaryKind = other(r.primaryKind);

  const over = primary.over;
  const width = primary.width;

  return (
    <div className="grid grid-cols-[130px_1fr_112px] items-center gap-3 sm:grid-cols-[150px_1fr_136px]">
      <span className="flex min-w-0 items-center gap-2.5">
        <span
          className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-xl font-display text-[11px] font-bold text-white ${
            r.capacity === 0
              ? "bg-gray-200 text-gray-500 dark:bg-navy-900 dark:text-gray-500"
              : "bg-gradient-to-br from-dv-blue-500 to-[#7A2BD6]"
          }`}
          aria-hidden="true"
        >
          {initials(r.name)}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-[12.5px] font-semibold text-navy-700 dark:text-white">{r.name}</span>
          <span className="block text-[10.5px] text-gray-500 dark:text-gray-400">
            {r.role || "—"}
            {r.capacity > 0 && ` · ${r.capacity.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} h`}
          </span>
        </span>
      </span>

      <span className="relative block">
        {secondary ? (
          <span className="flex flex-col gap-[3px]">
            <Track width={width} thin className={barTone(r.primaryKind, over)} />
            <Track width={secondary.width} thin className={barTone(secondaryKind, secondary.over)} />
          </span>
        ) : (
          <Track width={width} className={barTone(r.primaryKind, over)} />
        )}
        {r.capacity > 0 && (
          <span
            className="absolute -top-0.5 bottom-[-2px] w-0.5 rounded bg-gray-400 dark:bg-gray-300"
            style={{ left: `${CAP_TICK}%` }}
            aria-hidden="true"
          />
        )}
      </span>

      <span className="text-right text-[11.5px] tabular-nums text-gray-600 dark:text-gray-300">
        {r.capacity === 0 ? (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-gray-500 dark:bg-navy-900 dark:text-gray-400">
            sem capacidade
          </span>
        ) : (
          <>
            <b className={`font-display ${over ? "text-red-600 dark:text-red-400" : "text-navy-700 dark:text-white"}`}>
              {Math.round(r.pct)}%
            </b>{" "}
            · {hrs(r.value)} h
            {secondary && (
              <span
                className={`block text-[10.5px] ${
                  secondaryKind === "worked"
                    ? secondary.over
                      ? "text-amber-600 dark:text-amber-400"
                      : "text-emerald-600 dark:text-emerald-400"
                    : secondary.over
                      ? "text-red-600 dark:text-red-400"
                      : "text-dv-blue-600 dark:text-dv-cyan-300"
                }`}
              >
                {hrs(r.secondary ?? 0)} h {SERIES_LABEL[secondaryKind]}
              </span>
            )}
            {r.flag && (
              <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                {r.flag}
              </span>
            )}
            {r.delta !== undefined && (
              <span className="block">
                <DeltaChip value={r.delta} />
              </span>
            )}
          </>
        )}
      </span>
    </div>
  );
};

interface Props {
  view: "week" | "month";
  weekRows?: TeamWeekLoad[] | null;
  monthRows?: EmployeeHours[] | null;
  previousLabel?: string;
}

const TeamLoadList: React.FC<Props> = ({ view, weekRows, monthRows, previousLabel }) => {
  const rows: Row[] =
    view === "week"
      ? (weekRows ?? []).map((e) => ({
          id: e.employeeId,
          name: e.employeeName,
          role: null,
          capacity: e.capacityHours,
          value: e.plannedHours,
          pct: e.utilizationPct,
          primaryKind: "planned" as SeriesKind,
          secondary: e.workedHours ?? 0,
        }))
      : (monthRows ?? []).map((e) => ({
          id: e.employeeId,
          name: e.employeeName,
          role: e.jobDescription,
          capacity: e.capacityMtd,
          value: e.workedMtd,
          pct: e.utilizationPct,
          primaryKind: "worked" as SeriesKind,
          secondary: e.plannedMtd ?? 0,
          delta: e.deltaPct,
          flag: e.noTimesheetLastWeek ? "sem apont." : undefined,
        }));

  return (
    <Card extra="p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-navy-700 dark:text-white">
            {view === "week" ? "Carga da semana por pessoa" : "Horas por colaborador"}
          </h3>
          <p className="mb-2 text-xs text-gray-700 dark:text-gray-300">
            {view === "week"
              ? "Planejado e apontado sobre a mesma capacidade individual — o vazio é ociosidade, a diferença entre as duas barras é o que o cronograma não previu."
              : `Apontado e planejado sobre o capacity individual do mês até hoje — a barra de cima responde pelo percentual, com a variação contra ${previousLabel ?? "o mês anterior"}.`}
          </p>
          <p className="mb-4 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[10.5px] text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1.5">
              <i className="h-2 w-3.5 rounded-sm bg-gradient-to-r from-dv-blue-500 to-dv-cyan-400" />
              planejado
            </span>
            <span className="flex items-center gap-1.5">
              <i className="h-2 w-3.5 rounded-sm bg-gradient-to-r from-emerald-500 to-emerald-400" />
              apontado
            </span>
            <span className="flex items-center gap-1.5">
              <i className="h-2 w-3.5 rounded-sm bg-gradient-to-r from-[#E05252] to-red-400" />
              planejado acima da capacidade
            </span>
            <span className="flex items-center gap-1.5">
              <i className="h-2 w-3.5 rounded-sm bg-gradient-to-r from-amber-500 to-amber-400" />
              apontado acima da capacidade
            </span>
          </p>
        </div>
        <Link
          to="/admin/capacity"
          className="shrink-0 text-xs font-semibold text-dv-blue-600 hover:underline dark:text-dv-cyan-300"
        >
          Capacity →
        </Link>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 py-8 text-center dark:border-gray-700">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Sem colaboradores no período.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          {rows.map((r) => (
            <Bar key={r.id} r={r} />
          ))}
        </div>
      )}
    </Card>
  );
};

export default TeamLoadList;
